import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';

/* ── Brand-colour social icons (inline SVG, no external deps) ── */
const IconFacebook = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="#1877F2">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971h-1.513c-1.491 0-1.956.93-1.956 1.885v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
  </svg>
);

const IconInstagram = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <defs>
      <radialGradient id="ig-rg" cx="30%" cy="107%" r="150%">
        <stop offset="0%"  stopColor="#fdf497"/>
        <stop offset="5%"  stopColor="#fdf497"/>
        <stop offset="45%" stopColor="#fd5949"/>
        <stop offset="60%" stopColor="#d6249f"/>
        <stop offset="90%" stopColor="#285AEB"/>
      </radialGradient>
    </defs>
    <rect width="24" height="24" rx="6" fill="url(#ig-rg)"/>
    <path d="M12 7.2A4.8 4.8 0 1 0 12 16.8 4.8 4.8 0 0 0 12 7.2zm0 7.92A3.12 3.12 0 1 1 12 8.88a3.12 3.12 0 0 1 0 6.24z" fill="#fff"/>
    <circle cx="17.04" cy="6.96" r="1.12" fill="#fff"/>
    <path d="M16.8 2.4H7.2A4.8 4.8 0 0 0 2.4 7.2v9.6A4.8 4.8 0 0 0 7.2 21.6h9.6a4.8 4.8 0 0 0 4.8-4.8V7.2A4.8 4.8 0 0 0 16.8 2.4zm3.12 14.4a3.12 3.12 0 0 1-3.12 3.12H7.2a3.12 3.12 0 0 1-3.12-3.12V7.2A3.12 3.12 0 0 1 7.2 4.08h9.6A3.12 3.12 0 0 1 19.92 7.2v9.6z" fill="#fff"/>
  </svg>
);

const IconGmail = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/>
    <path d="M0 5.457v13.909c0 .904.732 1.636 1.636 1.636h3.819V11.73L0 7.64V5.457z" fill="#C5221F"/>
    <path d="M24 5.457v2.183l-5.455 4.09V21h3.819A1.636 1.636 0 0 0 24 19.366V5.457z" fill="#C5221F"/>
    <path d="M0 7.64l5.455 4.09V4.64L3.927 3.493C2.309 2.28 0 3.434 0 5.457V7.64z" fill="#F6AD01"/>
    <path d="M24 7.64l-5.455 4.09V4.64l1.528-1.147C21.69 2.28 24 3.434 24 5.457V7.64z" fill="#F6AD01"/>
    <path d="M5.455 11.73L12 16.64l6.545-4.91V4.64L12 9.548 5.455 4.64v7.09z" fill="#4285F4"/>
  </svg>
);

const IconWhatsApp = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
    <circle cx="12" cy="12" r="12" fill="#25D366"/>
    <path fill="#fff" d="M17.04 6.93A6.93 6.93 0 0 0 5.07 15.1L4 20l5.05-1.32a6.93 6.93 0 0 0 3.31.84h.01A6.93 6.93 0 0 0 17.04 6.93zm-4.67 10.66a5.75 5.75 0 0 1-2.93-.8l-.21-.12-2.18.57.58-2.13-.14-.22a5.76 5.76 0 1 1 4.88 2.7zm3.16-4.31c-.17-.09-.99-.49-1.14-.54-.15-.06-.26-.08-.38.08-.11.17-.44.54-.54.66-.1.11-.2.13-.37.04a4.7 4.7 0 0 1-1.38-.85 5.2 5.2 0 0 1-.96-1.19c-.1-.17-.01-.27.08-.36.08-.08.17-.2.26-.3.09-.1.12-.17.17-.28.06-.11.03-.21-.01-.3-.05-.08-.38-.91-.52-1.25-.14-.33-.28-.28-.38-.29h-.32c-.11 0-.3.04-.45.21-.16.17-.6.59-.6 1.43s.61 1.66.7 1.77c.08.12 1.2 1.83 2.9 2.57.41.17.72.28.97.36.41.13.78.11 1.07.07.33-.05.99-.4 1.13-.8.14-.38.14-.71.1-.78-.05-.07-.16-.11-.33-.2z"/>
  </svg>
);

const Navbar: React.FC = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { name: "About Us",     href: "/about"        },
    { name: "Destinations", href: "/destinations" },
    { name: "Heli Tours",   href: "/heli-tours"   },
    { name: "Services",     href: "/services"     },
    { name: "Blog",         href: "/blog"         },
    { name: "Contact Us",   href: "/contact"      },
  ];

  const SocialIcons = () => (
    <div className="flex items-center gap-3">
      <a href="https://www.facebook.com/profile.php?id=61573665246799" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:scale-110 transition-transform"><IconFacebook /></a>
      <a href="https://www.instagram.com/trekmatenepal/?hl=en" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:scale-110 transition-transform"><IconInstagram /></a>
      <a href="https://wa.me/9779763474895" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="hover:scale-110 transition-transform"><IconWhatsApp /></a>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* ───────── TOP PROMO BAR ───────── */}
      <div className="bg-slate-900 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-between h-10">
          <a
            href="mailto:trekmatenepal@gmail.com"
            className="hidden md:flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <IconGmail />
            <span className="text-xs">trekmatenepal@gmail.com</span>
          </a>

          <div className="flex-1 text-center text-xs md:text-sm font-medium px-2 truncate">
            <span className="hidden sm:inline">20% off on your next Trip. Hurry up and book a tour before the time is up! T&amp;C Applied.</span>
          </div>

          <div className="flex items-center gap-4">
            {/* Phone */}
            <a
              href="tel:+9779763474895"
              className="flex items-center gap-2 hover:text-orange-400 transition-colors"
            >
              <span className="bg-orange-500 p-1 rounded-full">
                <Phone className="h-3 w-3" />
              </span>
              <span className="text-xs font-semibold">+977 9763474895</span>
            </a>

            {/* Socials */}
            <div className="hidden md:flex items-center gap-3 pl-4 border-l border-white/20">
              <SocialIcons />
            </div>
          </div>
        </div>
      </div>

      {/* ───────── MAIN NAV ───────── */}
      <div className="bg-white shadow-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-between h-20 gap-4">

          {/* Logo */}
          <Link to="/" className="shrink-0 transition-transform hover:scale-105">
            <img src="/logo192.png" alt="TrekMate Nepal" className="h-14 w-auto" />
          </Link>

          {/* Nav Links */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`relative py-1.5 text-[14px] font-semibold transition-all duration-300 group whitespace-nowrap ${
                    isActive ? "text-orange-500" : "text-slate-700 hover:text-slate-900"
                  }`}
                >
                  {link.name}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-orange-500 transition-all duration-300 ${
                    isActive ? "w-full" : "w-0 group-hover:w-full"
                  }`} />
                </Link>
              );
            })}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Book Now Button */}
            <Link
              to="/booking"
              className="hidden md:inline-flex bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-md whitespace-nowrap"
            >
              Book Now
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-slate-700"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-100 bg-white">
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.href;
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`py-3 px-4 rounded-lg text-[15px] font-semibold transition-all ${
                      isActive
                        ? "bg-orange-50 text-orange-500"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}

              <Link
                to="/booking"
                onClick={() => setMobileOpen(false)}
                className="mt-3 bg-orange-500 hover:bg-orange-600 text-white text-center font-semibold px-5 py-3 rounded-full transition-colors shadow-md"
              >
                Book Now
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
